import { MutationFunction, useMutation, useQueryClient } from "react-query";

import { makeRequest } from "../../util";
import {
  WebTrackerResolutionResponse,
  WebTrackerResolutionType,
} from "../types";
import useProductEventEmitter from "../instrumentation/useProductEventEmitter";

export type addWebTrackerResolutionVariables = {
  trackerDomain: string;
  trackerName: string;
  oldResolutionType: WebTrackerResolutionType;
  newResolutionType: WebTrackerResolutionType;
  note: string;
};

async function addWebTrackerResolution({
  trackerDomain,
  newResolutionType,
  note,
}: addWebTrackerResolutionVariables): Promise<WebTrackerResolutionResponse> {
  let res = await makeRequest<WebTrackerResolutionResponse>(
    "add_web_tracker_resolution",
    {
      trackerDomain,
      resolutionType: newResolutionType,
      note,
    }
  );
  return {
    webTrackerResolution: {
      ...res.data.webTrackerResolution,
      updatedAt: new Date(res.data.webTrackerResolution.updatedAt),
    },
    user: res.data.user,
  };
}

export default function useAddWebTrackerResolution(
  onSuccess: () => void,
  onSettled?: () => void
) {
  const queryClient = useQueryClient();
  const productEventEmitter = useProductEventEmitter();

  return useMutation<
    WebTrackerResolutionResponse,
    unknown,
    addWebTrackerResolutionVariables
  >(
    addWebTrackerResolution as MutationFunction<
      WebTrackerResolutionResponse,
      addWebTrackerResolutionVariables
    >,
    {
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries(
          `web-tracker-resolution-${variables.trackerDomain}`
        );

        void queryClient.invalidateQueries(`web-tracker-latest-report`);

        void productEventEmitter.trackResolutionChanged(
          variables.trackerName,
          variables.trackerDomain,
          variables.oldResolutionType,
          variables.newResolutionType
        );

        onSuccess();
      },
      onSettled: onSettled,
    }
  );
}
